"use client";

import React from "react";
import { Check, Mail, Phone, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const benefits = ["24/7 AI-powered site generation", "Managed WordPress hosting included", "Drag-and-drop visual editing", "Automated SSL and daily backups", "Expert human support when needed"];
const productOptions = ["General Inquiry", "Technical Support", "Billing Question"];
function QuoteFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
          <Check className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-bold font-default text-foreground">Message Sent</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Your Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Your Name <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Jane Smith" className="bg-white text-black rounded-lg border-none" {...field} />
              </FormControl>
              <FormDescription className="text-gray-500">Full name or business name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Website URL</FormLabel>
              <FormControl>
                <Input placeholder="yourdomain.com" className="bg-white text-black rounded-lg border-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Email Address <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@webshingle.com" className="bg-white text-black rounded-lg border-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-white text-black rounded-lg border-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="product" rules={{
        required: "Please select a subject"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Subject <span className="text-primary">*</span></FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-white text-black rounded-lg border-none">
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {productOptions.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="quantity" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Priority Level</FormLabel>
              <FormControl>
                <Input type="text" placeholder="e.g., Urgent, Normal" className="bg-white text-black rounded-lg border-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" rules={{
      required: "Message details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">How can we help? <span className="text-primary">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your request or issue in detail..." className="min-h-[120px] resize-y bg-white text-black rounded-lg border-none" {...field} />
            </FormControl>
            <FormDescription className="text-gray-500">Our team typically responds within 2 business hours.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" size="lg" className="w-full text-base font-bold bg-primary text-primary-foreground hover:opacity-90 rounded-lg shadow-lg" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending Message...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function RequestATechnicalQuote() {
  return <section data-section-id="223513" id="direct-support" className="w-full bg-background dark py-16 md:py-24 px-6 md:px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        
        {}
        <div className="flex flex-col space-y-8 lg:pr-8">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold font-default text-foreground tracking-tighter mb-4">
              Direct Support for Your Vision
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Whether you're stuck on a prompt or need help scaling your WordPress site, our team is here to ensure your Webshingle experience is seamless.
            </p>
          </div>

          <ul className="space-y-4">
            {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                <div className="mt-1 shrink-0 bg-primary/20 p-1 rounded-lg">
                  <Check className="size-5 text-primary" />
                </div>
                <span className="text-base text-foreground font-medium pt-1">
                  {benefit}
                </span>
              </li>)}
          </ul>

          <div className="pt-8 border-t border-border mt-8">
            <h3 className="text-xl font-semibold font-default text-foreground mb-4">
              Support Channels
            </h3>
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex items-center gap-3">
                <div className="bg-secondary/20 p-2 rounded-lg">
                  <Phone className="size-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Support Hours</p>
                  <p className="text-foreground font-medium">
                    9am - 5pm EST
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-secondary/20 p-2 rounded-lg">
                  <Mail className="size-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email Support</p>
                  <Link to="mailto:support@webshingle.com" className="text-foreground font-medium hover:text-primary transition-colors">
                    support@webshingle.com
                  </Link>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/ContactSupportPage" className="text-primary font-bold hover:underline flex items-center gap-2">
                Launch Live Chat Support →
              </Link>
            </div>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute -inset-4 bg-primary/10 rounded-3xl -z-10 blur-3xl hidden lg:block" />
          
          <Card className="border-border shadow-2xl bg-card overflow-hidden rounded-3xl">
            <CardHeader className="bg-muted/20 border-b border-border pb-6">
              <CardTitle className="text-2xl font-bold font-default text-foreground">
                Send a Support Request
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground">
                Fill out the form below and our WordPress experts will get back to you shortly.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <FormProvider formKey="direct_support_form_contact_support" formId="direct_support_form_contact_support" sectionName="DirectSupport" defaultValues={{
              name: "",
              company: "",
              email: "",
              phone: "",
              product: "",
              quantity: "",
              details: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              product: {
                required: true
              },
              details: {
                required: true
              }
            }} submitText="Send Message" successMessage="Your message has been received. We'll be in touch within 2 business hours.">
                <QuoteFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>

      </div>
    </section>;
}